import React from "react";
import { Link } from "react-router-dom";
import { Apple, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openExternalUrl } from "@/hooks/useExternalLink";
import { APP_STORE_URL, PLAY_STORE_URL, shouldShowAppStore, shouldShowPlayStore } from "@/lib/storeLinks";
import Reveal from "./Reveal";
import PhoneFrame from "./PhoneFrame";
import {
  RoomsScreen,
  VentScreen,
  ReplyScreen,
  JournalScreen,
  ChecklistScreen,
  CopingScreen,
  DirectoryScreen,
} from "./AppScreens";


/* ---------------------------------- Hero --------------------------------- */

export const HeroSection = ({ showStoreLinks }: { showStoreLinks: boolean }) => (
  <section className="relative overflow-hidden pt-32 pb-20 md:pt-48 md:pb-32">
    <div className="pointer-events-none absolute inset-x-0 top-0 h-[70vh] bg-gradient-to-b from-mist/60 to-background" />
    <div className="relative mx-auto max-w-7xl px-5 md:px-10">
      <Reveal className="max-w-4xl">
        <p className="font-display uppercase tracking-[0.35em] text-xs text-brand">
          Designed to help you let it out
        </p>
        <h1 className="mt-6 font-display uppercase text-[3.25rem] leading-[0.92] tracking-tight text-foreground sm:text-7xl lg:text-8xl">
          EXPRESS YOURSELF
          <span className="block text-brand">ANONYMOUSLY &amp; SAFELY</span>
        </h1>
        <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
          Topic-based rooms for venting, stress relief, and mental wellness. Find peer support from
          others who truly understand what you're going through.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-5">
          <Link to="/auth">
            <Button
              size="lg"
              className="rounded-full px-8 text-base transition-transform duration-300 hover:-translate-y-0.5"
            >
              Get Started
            </Button>
          </Link>
          {showStoreLinks && shouldShowAppStore() && (
            <button
              type="button"
              onClick={() => openExternalUrl(APP_STORE_URL)}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            >
              <Apple className="h-4 w-4" />
              Download on the App Store
            </button>

          )}
          {showStoreLinks && shouldShowPlayStore() && (
            <button
              type="button"
              onClick={() => openExternalUrl(PLAY_STORE_URL)}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            >
              <Play className="h-4 w-4" />
              Get it on Google Play
            </button>
          )}
        </div>
      </Reveal>
    </div>
  </section>
);


/* ------------------------------- How it works ----------------------------- */

const steps = [
  {
    label: "Step one",
    title: "Find your room",
    body: "Topic-based rooms for anxiety, relationships, work, grief and more. Sit with people going through something similar.",
    screen: <RoomsScreen />,
    alt: "Vent rooms list showing topic-based support rooms",
  },
  {
    label: "Step two",
    title: "Let it out",
    body: "Post a Vent in your own words. No polish required, no audience to perform for.",
    screen: <VentScreen />,
    alt: "Writing a Vent and seeing anonymous replies",
  },
  {
    label: "Step three",
    title: "Show up for someone",
    body: "Reply, relate, or simply let someone know they were heard. Support goes both ways here.",
    screen: <ReplyScreen />,
    alt: "Supportive replies from the Vent community",
  },
];

export const HowItWorksSection = ({
  videoUrl,
  showVideo,
}: {
  videoUrl: string;
  showVideo: boolean;
}) => (
  <section className="bg-paper py-24 md:py-32" aria-labelledby="how-heading">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <Reveal className="max-w-2xl">
        <p className="font-display uppercase tracking-[0.3em] text-xs text-brand">How it works</p>
        <h2
          id="how-heading"
          className="mt-4 font-display uppercase text-4xl leading-[0.95] tracking-tight text-foreground md:text-6xl"
        >
          A space that meets you where you are
        </h2>
      </Reveal>

      <div className="mt-16 space-y-24 md:space-y-32">
        {steps.map((step, i) => (
          <div
            key={step.title}
            className="grid items-center gap-12 lg:grid-cols-2"
          >
            <Reveal className={i % 2 === 1 ? "lg:order-2" : undefined}>
              <p className="font-display uppercase tracking-[0.3em] text-xs text-slate">
                {step.label}
              </p>
              <h3 className="mt-4 font-display uppercase text-3xl leading-tight tracking-tight text-foreground md:text-5xl">
                {step.title}
              </h3>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
                {step.body}
              </p>
            </Reveal>
            <Reveal delay={100} className={`flex justify-center ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <PhoneFrame label={step.alt}>{step.screen}</PhoneFrame>
            </Reveal>
          </div>
        ))}
      </div>

      {showVideo && (
        <Reveal className="mt-24 md:mt-32">
          <div className="mx-auto max-w-3xl text-center">
            <h3 className="font-display uppercase text-3xl tracking-tight text-foreground md:text-4xl">
              See Vent in action
            </h3>
          </div>
          <div className="relative mx-auto mt-8 aspect-[9/16] w-full max-w-sm overflow-hidden rounded-lg border border-border shadow-soft">
            <video
              src={videoUrl}
              title="Vent app walkthrough"
              className="absolute inset-0 h-full w-full object-cover"
              playsInline
              loop
              controls
              preload="metadata"
            />
          </div>
        </Reveal>
      )}
    </div>
  </section>
);

/* ------------------------------ Wellness tools ---------------------------- */

const tools = [
  {
    title: "Daily Journal",
    body: "A private place to write it down and notice how the weeks actually went.",
    screen: <JournalScreen />,
  },
  {
    title: "Self-Care Checklist",
    body: "Small, doable things — water, fresh air, one message to a friend.",
    screen: <ChecklistScreen />,
  },
  {
    title: "Coping Strategies",
    body: "Simple techniques for the moments that feel like too much.",
    screen: <CopingScreen />,
  },
];

export const WellnessSection = () => (
  <section className="py-24 md:py-32" aria-labelledby="wellness-heading">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <Reveal className="max-w-2xl">
        <p className="font-display uppercase tracking-[0.3em] text-xs text-brand">
          Wellness tools
        </p>
        <h2
          id="wellness-heading"
          className="mt-4 font-display uppercase text-4xl leading-[0.95] tracking-tight text-foreground md:text-6xl"
        >
          Quiet support between the conversations
        </h2>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
          Letting it out is the start. These are the everyday habits that help it stay lighter.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-14 md:grid-cols-3">
        {tools.map((tool, i) => (
          <Reveal key={tool.title} delay={i * 100}>
            <PhoneFrame className="max-w-[240px]" label={`${tool.title} screen`}>
              {tool.screen}
            </PhoneFrame>
            <h3 className="mt-8 text-center font-display uppercase text-2xl tracking-tight text-foreground">
              {tool.title}
            </h3>
            <p className="mx-auto mt-3 max-w-xs text-center text-sm leading-relaxed text-muted-foreground">
              {tool.body}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------- Provider directory -------------------------- */

export const TherapistSection = () => (
  <section className="bg-paper py-24 md:py-32" aria-labelledby="providers-heading">
    <div className="mx-auto max-w-7xl px-5 md:px-10 grid gap-14 lg:grid-cols-2 lg:items-center">
      <Reveal className="flex justify-center lg:order-2">
        <PhoneFrame label="Provider directory listings">
          <DirectoryScreen />
        </PhoneFrame>
      </Reveal>
      <Reveal delay={100} className="lg:order-1">
        <p className="font-display uppercase tracking-[0.3em] text-xs text-brand">
          When you want more
        </p>
        <h2
          id="providers-heading"
          className="mt-4 font-display uppercase text-4xl leading-[0.95] tracking-tight text-foreground md:text-6xl"
        >
          Licensed professionals, one step away
        </h2>
        <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
          Browse our directory of licensed mental health providers whenever you decide you'd like professional
          support.
        </p>
        <Link to="/auth" className="mt-8 inline-block">
          <Button
            variant="outline"
            size="lg"
            className="rounded-full px-7 transition-transform duration-300 hover:-translate-y-0.5"
          >
            Explore The Vent App
          </Button>
        </Link>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------- Partnerships ----------------------------- */

export const PartnershipsSection = () => (
  <section className="py-24 md:py-32" aria-labelledby="partners-heading">
    <div className="mx-auto max-w-7xl px-5 md:px-10 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
      <Reveal>
        <p className="font-display uppercase tracking-[0.3em] text-xs text-brand">Partnerships</p>
        <h2
          id="partners-heading"
          className="mt-4 font-display uppercase text-4xl leading-[0.95] tracking-tight text-foreground md:text-6xl"
        >
          Bring Vent to your community
        </h2>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
          We work with mental health providers, universities, employers, organizations, and wellness partners who
          want to give the people they serve somewhere to be honest.
        </p>
      </Reveal>
      <Reveal delay={100} className="lg:text-right">
        <Link to="/partnerships">
          <Button
            size="lg"
            className="rounded-full px-8 transition-transform duration-300 hover:-translate-y-0.5"
          >
            Partner With Us
          </Button>
        </Link>
      </Reveal>
    </div>
  </section>
);

/* --------------------------------- Final CTA ------------------------------ */

export const FinalCTA = ({ showStoreLinks }: { showStoreLinks: boolean }) => (
  <section className="bg-ink py-28 text-ink-foreground md:py-36">
    <div className="mx-auto max-w-7xl px-5 md:px-10 text-center">
      <Reveal>
        <h2 className="mx-auto max-w-3xl font-display uppercase text-4xl leading-[0.98] tracking-tight sm:text-6xl">
          Whatever you're holding, let it out.
        </h2>
        <div className="mt-10 flex flex-col items-center gap-5">
          <Link to="/auth">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 text-base transition-transform duration-300 hover:-translate-y-0.5"
            >
              Get Started
            </Button>
          </Link>
          {showStoreLinks && shouldShowAppStore() && (
            <button
              type="button"
              onClick={() => openExternalUrl(APP_STORE_URL)}
              className="inline-flex items-center gap-2 text-sm text-ink-foreground/70 transition-colors hover:text-ink-foreground"
            >
              <Apple className="h-4 w-4" />
              Download on the App Store
            </button>

          )}
          {showStoreLinks && shouldShowPlayStore() && (
            <button
              type="button"
              onClick={() => openExternalUrl(PLAY_STORE_URL)}
              className="inline-flex items-center gap-2 text-sm text-ink-foreground/70 transition-colors hover:text-ink-foreground"
            >
              <Play className="h-4 w-4" />
              Get it on Google Play
            </button>
          )}
        </div>
      </Reveal>
    </div>
  </section>
);
